import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationPrevious, PaginationNext } from "@/components/ui/pagination";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { Scale, Calendar, Building, Sparkles, Copy, Download, ChevronDown, ArrowUp, TrendingUp, CalendarClock } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useTutorial } from "@/hooks/useTutorial";
import { TutorialHighlight } from "@/components/tutorial";
import { SubscriptionOverlay } from "@/components/SubscriptionOverlay";
import { CaseSummaryModal } from "@/components/CaseSummaryModal";
import { getTrialStatus } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
interface Doctrina {
  id: number;
  texto: string;
}

interface SearchResult {
  fallo_id?: number;
  texto: string;
  metadata: {
    id: number;
    fallo_id: number;
    tribunal?: string;
    expediente?: string;
    expte?: string;
    caratula?: string;
    autos?: string;
    fecha: string;
    link?: string;
    download_url?: string;
    secretaria?: string;
    tesauro?: string;
    tomo?: number;
    pagina?: number;
  };
  doctrinas: Doctrina[];
  objeto?: {
    texto: string;
  };
}

interface SearchResultsProps {
  results: SearchResult[];
  collection?: string;
  isLoading?: boolean;
  onSearchDoctrina?: (text: string, collection: string) => void;
  onSearchObjeto?: (text: string, collection: string) => void;
  activeTab?: string;
  highlightTerms?: string[];
}

export function SearchResults({ results, collection, isLoading = false, onSearchDoctrina, onSearchObjeto, activeTab, highlightTerms = [] }: SearchResultsProps) {
  const { subscription } = useAuth();
  const { currentStep, completeStep } = useTutorial();
  const navigate = useNavigate();
  const isCsjn = collection === 'csjn';
  const isTsj = collection === 'tsj';
  const [openDoctrinas, setOpenDoctrinas] = useState<Set<number>>(new Set());
  const [openObjetos, setOpenObjetos] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showSubscriptionOverlay, setShowSubscriptionOverlay] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [sortBy, setSortBy] = useState<"relevance" | "date">("relevance");
  const [summaryModal, setSummaryModal] = useState<{
    isOpen: boolean, 
    falloId: string, 
    caratula: string,
    tribunal?: string,
    expediente?: string,
    fecha?: string,
    tipo?: string,
    downloadLink?: string,
    collection?: string
  }>({
    isOpen: false,
    falloId: '',
    caratula: '',
    tribunal: undefined,
    expediente: undefined,
    fecha: undefined,
    tipo: undefined,
    downloadLink: undefined,
    collection: undefined
  });
  const scrollAreaRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resultsTopRef = useRef<HTMLDivElement>(null);
  
  // Safety check: if trial is expired for free users, show premium overlay
  // Note: This is a fallback - in practice, SearchInterface blocks searches for expired trials
  const isFree = !subscription?.subscribed || subscription?.plan === 'free';
  const isMicro = subscription?.plan === 'micro';
  const { isTrialExpired, isTrialActive } = getTrialStatus(subscription);
  const isPremiumContent = isFree && isTrialExpired;
  const isFreeTrial = isFree && isTrialActive;
  
  // Sort results while preserving original rank
  const sortedResults = useMemo(() => {
    if (sortBy === "date") {
      return [...results].sort((a, b) => {
        const dateA = new Date(a.metadata.fecha).getTime();
        const dateB = new Date(b.metadata.fecha).getTime();
        return dateB - dateA; // Newest first
      });
    }
    return results; // Original order (by relevance)
  }, [results, sortBy]);
  
  const resultsPerPage = 10;
  const totalPages = Math.ceil(sortedResults.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = sortedResults.slice(startIndex, endIndex);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset collapsible states and pagination when results change (new search)
  useEffect(() => {
    setOpenDoctrinas(new Set());
    setOpenObjetos(new Set());
    setCurrentPage(1); // Reset to first page on new search
    setSortBy("relevance"); // Reset sort to relevance on new search
  }, [results]);

  // Reset collapsible states and scroll positions when pagination changes
  useEffect(() => {
    setOpenDoctrinas(new Set());
    setOpenObjetos(new Set());
    
    // Reset scroll positions of all ScrollArea components
    scrollAreaRefs.current.forEach(ref => {
      if (ref) {
        const viewport = ref.querySelector('[data-radix-scroll-area-viewport]');
        if (viewport) {
          viewport.scrollTop = 0;
        }
      }
    });
    
    // Scroll to top of results section
    if (resultsTopRef.current) {
      resultsTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  const SkeletonCard = () => (
    <Card className="shadow-card border-border bg-card">
      <CardHeader className="pb-4">
        <div className="space-y-3 mb-4">
          <div className="flex flex-wrap gap-6">
            <div className="flex flex-col gap-1">
              <Skeleton className="h-3 w-16" />
              <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30">
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-3 w-20" />
              <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30">
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Skeleton className="h-3 w-16" />
              <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30">
                <Skeleton className="h-3 w-3 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-4 mb-4">
          <Skeleton className="h-6 w-3/4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-36" />
          <Skeleton className="h-9 w-44" />
          <Skeleton className="h-9 w-28" />
          <Skeleton className="h-9 w-40" />
        </div>
      </CardContent>
    </Card>
  );

  if (results.length === 0 && !isLoading) {
    return null;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDoctrinaText = (text: string) => {
    if (!isCsjn) {
      return text;
    }

    // Pattern to match: KEYWORDS (uppercase) - main text - closing (between hyphens)
    // Look for text that starts with uppercase words and hyphens, followed by lowercase text, ending with text between hyphens
    const pattern = /^([A-ZÁÉÍÓÚÑ\s\-]+?)(\s+)([A-Za-z].*?)(\s*-[^-]*-\s*)$/;
    const match = text.match(pattern);
    
    if (match) {
      const [, keywords, , mainText, closing] = match;
      // Add blank lines between sections
      return `${keywords.trim()}\n\n${mainText.trim()}\n\n${closing.trim()}`;
    }
    
    return text;
  };

  // Highlight matched filter terms in text
  const highlightText = (text: string) => {
    if (!text || highlightTerms.length === 0) {
      return text;
    }

    // Create a regex pattern that matches any of the highlight terms (case-insensitive)
    const pattern = highlightTerms
      .map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape special regex chars
      .join('|');
    
    if (!pattern) return text;

    const regex = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches any highlight term (case-insensitive)
      const isMatch = highlightTerms.some(term => 
        part.toLowerCase() === term.toLowerCase()
      );

      if (isMatch) {
        return (
          <span 
            key={index} 
            className="bg-warning/20 px-0.5 rounded"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const toggleDoctrinas = (resultIndex: number) => {
    const newOpen = new Set(openDoctrinas);
    if (newOpen.has(resultIndex)) {
      newOpen.delete(resultIndex);
    } else {
      newOpen.add(resultIndex);
    }
    setOpenDoctrinas(newOpen);
  };

  const toggleObjetos = (resultIndex: number) => {
    const newOpen = new Set(openObjetos);
    if (newOpen.has(resultIndex)) {
      newOpen.delete(resultIndex);
    } else {
      newOpen.add(resultIndex);
    }
    setOpenObjetos(newOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const PaginationControls = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center py-6">
        <Pagination>
          <PaginationContent className="gap-2">
            <PaginationItem>
              <PaginationPrevious 
                href="#" 
                onClick={(e) => { 
                  e.preventDefault(); 
                  if (currentPage > 1) { 
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setCurrentPage(currentPage - 1);
                      setIsTransitioning(false);
                    }, 150);
                  } 
                }}
                className={`hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 rounded-lg ${currentPage <= 1 ? 'opacity-50 pointer-events-none' : ''}`}
              />
            </PaginationItem>
            <PaginationItem>
              <div className="px-4 py-2 text-sm text-muted-foreground bg-secondary/30 rounded-lg backdrop-blur-sm">
                Página {currentPage} de {totalPages}
              </div>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext 
                href="#" 
                onClick={(e) => { 
                  e.preventDefault(); 
                  if (currentPage < totalPages) { 
                    setIsTransitioning(true);
                    setTimeout(() => {
                      setCurrentPage(currentPage + 1);
                      setIsTransitioning(false);
                    }, 150);
                  } 
                }}
                className={`hover:bg-primary/10 hover:border-primary/30 transition-all duration-300 rounded-lg ${currentPage >= totalPages ? 'opacity-50 pointer-events-none' : ''}`}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-results-section ref={resultsTopRef}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Scale className="h-6 w-6 text-primary" />
          Resultados de búsqueda
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {!isLoading && (
            <>
              <span className="text-sm text-muted-foreground font-medium">
                {sortBy === "relevance" 
                  ? "Resultados ordenados por relevancia" 
                  : "Resultados ordenados por fecha"}
              </span>
              <Button
                size="sm"
                onClick={() => setSortBy(sortBy === "relevance" ? "date" : "relevance")}
                className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all duration-300"
              >
                {sortBy === "relevance" ? (
                  <>
                    <CalendarClock className="h-4 w-4" />
                    Ordenar por fecha
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    Ordenar por relevancia
                  </>
                )}
              </Button>
            </>
          )}
          {isLoading ? (
            <Skeleton className="h-6 w-32" />
          ) : (
            <Badge variant="secondary" className="text-sm">
              {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Top Pagination */}
      <PaginationControls />

      <div className={`grid gap-8 transition-all duration-300 ${isTransitioning ? 'opacity-50 scale-[0.98]' : 'opacity-100 scale-100'}`}>
        {isLoading ? (
          // Show skeleton cards while loading
          Array.from({ length: 3 }).map((_, index) => (
            <div key={`skeleton-${index}`} className="animate-fade-in">
              <SkeletonCard />
            </div>
          ))
        ) : (
          currentResults.map((result, index) => {
            // Calculate original rank based on position in original results array
            const originalRank = results.findIndex(r => r.metadata.fallo_id === result.metadata.fallo_id) + 1;
            const absoluteIndex = startIndex + index; // Index in the full sorted results
            const isBlurred = false; // Trial users now see all results
            
            return (
              <div key={`${currentPage}-${index}`} className="animate-fade-in">
                <Card className={`shadow-card border-border bg-card hover:shadow-card-hover transition-shadow duration-200 group relative overflow-hidden ${isPremiumContent || isBlurred ? 'cursor-pointer' : ''}`}
                      onClick={(isPremiumContent || isBlurred) ? () => {
                        if (isBlurred) {
                          navigate('/planes');
                        } else {
                          setShowSubscriptionOverlay(true);
                        }
                      } : undefined}>
                  {/* Relevance Rank Badge */}
                  <div className="absolute top-4 right-4 z-20">
                    <Badge 
                      variant="secondary" 
                      className="text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shadow-sm"
                    >
                      #{originalRank}
                    </Badge>
                  </div>
                  
                  {(isPremiumContent || isBlurred) && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px] z-10 flex items-center justify-center">
                      <div className="bg-gradient-to-r from-primary/90 to-accent/90 text-white px-6 py-3 rounded-lg shadow-lg border border-white/20">
                        <p className="text-sm font-semibold text-center">
                          {isPremiumContent ? '🔒 Contenido Premium - Hacé clic para suscribirte' : '🔒 Suscribite para desbloquear!'}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-background/50 z-10"></div>
                
                <CardHeader className="pb-4 relative z-10">
                  {/* Metadata */}
                  <div className="space-y-3 mb-4">
                <div className="flex flex-wrap gap-6 text-sm">
                  {/* Tribunal or Fallos section */}
                  {isCsjn ? (
                    // CSJN format - show Fallos if tomo and pagina exist and are not 0
                    result.metadata.tomo && result.metadata.pagina && result.metadata.tomo !== 0 && result.metadata.pagina !== 0 ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-primary uppercase tracking-wide">Fallos</span>
                        <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30 group-hover:border-primary/30 transition-all duration-300">
                          <Building className="h-3 w-3 text-primary-glow" />
                          <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                            Fallos: {result.metadata.tomo}:{result.metadata.pagina}
                          </span>
                        </div>
                      </div>
                    ) : null
                  ) : (
                    // Regular format - show tribunal
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">Tribunal</span>
                      <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30 group-hover:border-primary/30 transition-all duration-300">
                        <Building className="h-3 w-3 text-primary-glow" />
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                          {isTsj ? "TSJ" : result.metadata.tribunal}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Secretaría - TSJ only */}
                  {isTsj && result.metadata.secretaria && (
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-medium text-primary uppercase tracking-wide">Secretaría</span>
                      <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30 group-hover:border-primary/30 transition-all duration-300">
                        <Building className="h-3 w-3 text-primary-glow" />
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">{result.metadata.secretaria}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-primary uppercase tracking-wide">Expediente</span>
                    <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30 group-hover:border-primary/30 transition-all duration-300">
                      <Scale className="h-3 w-3 text-primary-glow" />
                      <span className="font-medium text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                        {isTsj ? result.metadata.expte : result.metadata.expediente}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-primary uppercase tracking-wide">Fecha</span>
                    <div className="flex items-center gap-2 bg-card/30 backdrop-blur-sm px-3 py-1.5 rounded-full border border-border/30 group-hover:border-primary/30 transition-all duration-300">
                      <Calendar className="h-3 w-3 text-primary-glow" />
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors duration-300">{formatDate(result.metadata.fecha)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0 relative z-10">
              {/* Main content */}
              <ScrollArea 
                key={`scroll-${currentPage}-${index}`}
                className="h-40 mb-4"
                ref={(el) => {
                  if (scrollAreaRefs.current) {
                    scrollAreaRefs.current[index] = el;
                  }
                }}
              >
                <div className={`space-y-4 text-justify leading-relaxed ${isPremiumContent || isBlurred ? 'filter blur-sm' : ''}`}>
                  <h3 className="font-bold text-foreground text-lg leading-tight">
                    {isCsjn ? result.metadata.autos : result.metadata.caratula}
                  </h3>
                  <p className="text-foreground group-hover:text-foreground/90 transition-colors duration-300 whitespace-pre-line">
                    {highlightText(formatDoctrinaText(result.texto))}
                  </p>
                </div>
              </ScrollArea>

              {/* Action buttons */}
              <div className={`mb-4 flex flex-wrap gap-2 ${isPremiumContent || isBlurred ? 'opacity-50 pointer-events-none' : ''}`}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (isPremiumContent || isBlurred) {
                      if (isBlurred) {
                        navigate('/planes');
                      } else {
                        setShowSubscriptionOverlay(true);
                      }
                     } else {
                       const downloadUrl = isCsjn 
                         ? `https://sjconsulta.csjn.gov.ar/sjconsulta${result.metadata.link}` 
                         : isTsj 
                           ? result.metadata.download_url 
                           : result.metadata.link;
                        setSummaryModal({
                          isOpen: true,
                          falloId: result.metadata.fallo_id.toString(),
                          caratula: isCsjn ? result.metadata.autos || '' : result.metadata.caratula || '',
                          tribunal: isTsj ? "TSJ" : result.metadata.tribunal,
                          expediente: isTsj ? result.metadata.expte : result.metadata.expediente,
                          fecha: result.metadata.fecha,
                          tipo: isCsjn ? 'csjn' : 'camara',
                          downloadLink: downloadUrl,
                          collection: collection
                        });
                      }
                    }}
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Resumir fallo con IA</span>
                </Button>

                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    (isPremiumContent || isBlurred) ? (isBlurred ? navigate('/planes') : setShowSubscriptionOverlay(true)) : toast({
                      title: "Redactar argumento",
                      description: "Función en desarrollo! Estará disponible a la brevedad!",
                    })
                  }
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Redactar argumento con IA</span>
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (isPremiumContent || isBlurred) {
                      if (isBlurred) {
                        navigate('/planes');
                      } else {
                        setShowSubscriptionOverlay(true);
                      }
                      return;
                    }
                    
                    // Always search for objetos
                    if (!onSearchObjeto || !collection) return;
                    onSearchObjeto(result.texto, collection);
                  }}
                >
                  Buscar objetos procesales similares
                </Button>

                {/* Copy Citation Button with Tutorial Highlight */}
                {currentStep === "copy_citation" ? (
                  <TutorialHighlight 
                    step="copy_citation" 
                    tooltipContent="Hacé clic acá para copiar la cita"
                    tooltipSide="bottom"
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (isPremiumContent || isBlurred) {
                          if (isBlurred) {
                            navigate('/planes');
                          } else {
                            setShowSubscriptionOverlay(true);
                          }
                          return;
                        }
                        
                        let text;
                        if (isCsjn) {
                          if (result.metadata.tomo && result.metadata.pagina && result.metadata.tomo > 0 && result.metadata.pagina > 0) {
                            text = `Fallos: ${result.metadata.tomo}:${result.metadata.pagina}`;
                          } else {
                            text = `"${result.metadata.autos}", causa ${result.metadata.expediente}, ${formatDate(result.metadata.fecha)}`;
                          }
                        } else if (isTsj) {
                          text = `TSJ: "${result.metadata.caratula}", causa ${result.metadata.expte}, ${formatDate(result.metadata.fecha)}${result.metadata.secretaria ? `, Secretaría: ${result.metadata.secretaria}` : ''}`;
                        } else {
                          const title = result.metadata.caratula;
                          const source = result.metadata.tribunal;
                          text = `${source}: "${title}", causa ${result.metadata.expediente}, ${formatDate(result.metadata.fecha)}`;
                        }
                        
                        navigator.clipboard.writeText(text);
                        toast({ description: "Cita copiada al portapapeles." });
                        
                        // Complete tutorial step
                        completeStep("copy_citation");
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      <span>Copiar cita</span>
                    </Button>
                  </TutorialHighlight>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (isPremiumContent || isBlurred) {
                        if (isBlurred) {
                          navigate('/planes');
                        } else {
                          setShowSubscriptionOverlay(true);
                        }
                        return;
                      }
                      
                      let text;
                      if (isCsjn) {
                        if (result.metadata.tomo && result.metadata.pagina && result.metadata.tomo > 0 && result.metadata.pagina > 0) {
                          text = `Fallos: ${result.metadata.tomo}:${result.metadata.pagina}`;
                        } else {
                          text = `"${result.metadata.autos}", causa ${result.metadata.expediente}, ${formatDate(result.metadata.fecha)}`;
                        }
                      } else if (isTsj) {
                        text = `TSJ: "${result.metadata.caratula}", causa ${result.metadata.expte}, ${formatDate(result.metadata.fecha)}${result.metadata.secretaria ? `, Secretaría: ${result.metadata.secretaria}` : ''}`;
                      } else {
                        const title = result.metadata.caratula;
                        const source = result.metadata.tribunal;
                        text = `${source}: "${title}", causa ${result.metadata.expediente}, ${formatDate(result.metadata.fecha)}`;
                      }
                      
                      navigator.clipboard.writeText(text);
                      toast({ description: "Cita copiada al portapapeles." });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copiar cita</span>
                  </Button>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (isPremiumContent || isBlurred) {
                      if (isBlurred) {
                        navigate('/planes');
                      } else {
                        setShowSubscriptionOverlay(true);
                      }
                      return;
                    }
                    let link = isCsjn 
                      ? `https://sjconsulta.csjn.gov.ar/sjconsulta${result.metadata.link}` 
                      : isTsj 
                        ? result.metadata.download_url 
                        : result.metadata.link;
                    
                    // Fix TSJ URLs: replace HTTP with HTTPS
                    if (isTsj && link?.includes('http://www.tsjbaires.gov.ar')) {
                      link = link.replace('http://www.tsjbaires.gov.ar', 'https://www.tsjbaires.gov.ar');
                    }
                    
                    window.open(link, '_blank');
                  }}
                >
                  <Download className="h-4 w-4" />
                  <span>Descargar fallo completo</span>
                </Button>
              </div>

              {/* Objeto accordion */}
              {result.objeto && (
                <Collapsible 
                  open={openObjetos.has(index)} 
                  onOpenChange={() => toggleObjetos(index)}
                  className="mb-4"
                >
                  <div className="text-center">
                    <CollapsibleTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="flex items-center gap-2 text-base text-muted-foreground hover:text-foreground hover:bg-transparent hover:shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:scale-[1.01] transition-all duration-300 p-2 h-auto mx-auto border border-border/30 hover:border-primary"
                      >
                        <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${openObjetos.has(index) ? 'transform rotate-180' : ''}`} />
                        Ver objeto procesal del caso
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="pt-4">
                    <Card className="bg-secondary/10 border border-border/20 hover:bg-secondary/20 transition-colors duration-300">
                      <CardContent className="p-4">
                        <p className={`text-sm text-muted-foreground leading-relaxed text-justify ${isPremiumContent || isBlurred ? 'filter blur-sm' : ''} whitespace-pre-line mb-4`}>
                          {highlightText(formatDoctrinaText(result.objeto.texto))}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (isPremiumContent || isBlurred) {
                              if (isBlurred) {
                                navigate('/planes');
                              } else {
                                setShowSubscriptionOverlay(true);
                              }
                              return;
                            }
                            // Search for objetos when clicking this button
                            if (!onSearchObjeto || !collection) return;
                            onSearchObjeto(result.objeto!.texto, collection);
                          }}
                          className="w-full"
                        >
                          Buscar objetos procesales similares
                        </Button>
                      </CardContent>
                    </Card>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Doctrinas accordion - Hidden for premium content */}
              {!isPremiumContent && !isBlurred && result.doctrinas && result.doctrinas.length > 0 && (
                <Collapsible 
                  open={openDoctrinas.has(index)} 
                  onOpenChange={() => toggleDoctrinas(index)}
                >
                  <div className="text-center">
                    <CollapsibleTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="flex items-center gap-2 text-base text-muted-foreground hover:text-foreground hover:bg-transparent hover:shadow-[0_0_20px_hsl(var(--primary)/0.3)] hover:scale-[1.01] transition-all duration-300 p-2 h-auto mx-auto border border-border/30 hover:border-primary"
                      >
                        <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${openDoctrinas.has(index) ? 'transform rotate-180' : ''}`} />
                        Ver doctrinas ({result.doctrinas.length})
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="pt-4">
                    <div className="space-y-4">
                       {result.doctrinas.map((doctrina, doctrinaIndex) => (
                         <Card key={doctrina.id} className="bg-secondary/10 border border-border/20 hover:bg-secondary/20 transition-colors duration-300">
                           <CardContent className="p-4">
                              <p className="text-sm text-muted-foreground leading-relaxed text-justify whitespace-pre-line">
                                  {highlightText(formatDoctrinaText(doctrina.texto))}
                                </p>
                           </CardContent>
                         </Card>
                       ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                )}
              </CardContent>
            </Card>
          </div>
            );
          })
        )}
      </div>

      {/* Bottom Pagination */}
      <PaginationControls />

      {/* Floating Scroll to Top Button */}
      <Button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 rounded-full w-12 h-12 shadow-lg hover:shadow-xl transition-all duration-300 z-50 bg-primary text-primary-foreground hover:bg-primary/90 ${
          showScrollToTop 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-75 translate-y-2 pointer-events-none'
        }`}
        size="icon"
      >
        <ArrowUp className="h-5 w-5" />
      </Button>

      {/* Subscription Overlay */}
      {showSubscriptionOverlay && (
        <SubscriptionOverlay onClose={() => setShowSubscriptionOverlay(false)} />
      )}

      {/* Case Summary Modal */}
      <CaseSummaryModal
        isOpen={summaryModal.isOpen}
        onClose={() => setSummaryModal({ 
          isOpen: false, 
          falloId: '', 
          caratula: '',
          tribunal: undefined,
          expediente: undefined,
          fecha: undefined,
          tipo: undefined,
          downloadLink: undefined,
          collection: undefined
        })}
        falloId={summaryModal.falloId}
        caratula={summaryModal.caratula}
        tribunal={summaryModal.tribunal}
        expediente={summaryModal.expediente}
        fecha={summaryModal.fecha}
        tipo={summaryModal.tipo}
        downloadLink={summaryModal.downloadLink}
        collection={summaryModal.collection}
      />
    </div>
  );
}